import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Code2, Search, Settings, Plus,
  Filter, SortAsc, ChevronDown, ChevronUp,
  Loader2, X, Grid, List, Folder, Tag
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../lib/hooks/useAuth';
import { useNotes } from '../lib/hooks/useNotes';
import { useSubscription } from '../lib/hooks/useSubscription';
import { useUpdateStore } from '../store/update-store';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import ThemeToggle from '../components/ThemeToggle';
import NoteCard from '../components/NoteCard';
import NoteEditor from '../components/NoteEditor';
import EmptyState from '../components/EmptyState';
import LoadingState from '../components/LoadingState';
import ConfirmDialog from '../components/ConfirmDialog';
import ShareDialog from '../components/ui/ShareDialog';
import SubscriptionBadge from '../components/SubscriptionBadge';
import UpdateModal from '../components/UpdateModal';
import DisabledFeatureDialog from '../components/DisabledFeatureDialog';
import { useAuthStore } from '../store/auth-store';
import { supabase } from '../lib/supabase';
import updateData from '../data/updates.json';
import type { Note, ViewMode, SortOption } from '../types';

const SORT_OPTIONS: SortOption[] = [
  { id: 'updated-desc', label: 'Last Updated', field: 'updatedAt', order: 'desc' },
  { id: 'updated-asc', label: 'First Updated', field: 'updatedAt', order: 'asc' },
  { id: 'created-desc', label: 'Newest', field: 'createdAt', order: 'desc' },
  { id: 'created-asc', label: 'Oldest', field: 'createdAt', order: 'asc' },
  { id: 'title-asc', label: 'Title (A-Z)', field: 'title', order: 'asc' },
  { id: 'title-desc', label: 'Title (Z-A)', field: 'title', order: 'desc' },
];

const DashboardPage = () => {
  useAuth(); // Protect route
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { notes, isLoading, fetchNotes } = useNotes();
  const { subscription } = useSubscription();
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [sortOption, setSortOption] = useState<string>('updated-desc');
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<string | null>(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [noteToShare, setNoteToShare] = useState<Note | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [availableTags, setAvailableTags] = useState<any[]>([]);
  const [showDisabledDialog, setShowDisabledDialog] = useState(false);
  const { lastSeenVersion, setLastSeenVersion } = useUpdateStore();
  const [showUpdateModal, setShowUpdateModal] = useState(false);

  useEffect(() => {
    if (lastSeenVersion !== updateData.latestUpdate.version) {
      setShowUpdateModal(true);
    }
  }, [lastSeenVersion]);

  const handleUpdateModalClose = () => {
    setShowUpdateModal(false);
    setLastSeenVersion(updateData.latestUpdate.version);
  };

  const handleTagSelect = () => {
    setShowDisabledDialog(true);
  };

  useEffect(() => {
    const fetchUserSettings = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('user_settings')
          .select('view_mode')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error("Error fetching user settings:", error);
          return;
        }

        if (data && data.view_mode) {
          setViewMode(data.view_mode as ViewMode);
        }
      } catch (error: any) {
        console.error("Error fetching user settings:", error.message);
      }
    };

    fetchUserSettings();
  }, [user]);

  useEffect(() => {
    const fetchTags = async () => {
      if (!user) return;

      try {
        const { data: customTags, error } = await supabase
          .from('tags')
          .select('*')
          .eq('user_id', user.id);

        if (error) throw error;
        setAvailableTags(customTags || []);
      } catch (error) {
        console.error('Error fetching tags:', error);
        setAvailableTags([]);
      }
    };

    fetchTags();
  }, [user]);

  const updateViewMode = async (newViewMode: ViewMode) => {
    if (!user) return;

    setViewMode(newViewMode);

    try {
      const { error } = await supabase
        .from('user_settings')
        .upsert(
          { id: user.id, view_mode: newViewMode },
          { onConflict: 'id' }
        );

      if (error) {
        console.error("Error updating view mode:", error);
        toast.error("Failed to update view mode");
      }
    } catch (error: any) {
      console.error("Error updating view mode:", error.message);
      toast.error("Failed to update view mode");
    }
  };

  const handleCreateNote = async (noteParam: Partial<Note>) => {
    if (!user) {
      toast.error('You must be logged in to create notes');
      return false;
    }

    try {
      const { data, error } = await supabase
        .from('notes')
        .insert([{
          title: noteParam.title,
          content: noteParam.content,
          tags: noteParam.tags || [],
          user_id: user.id
        }])
        .select()
        .single();

      if (error) throw error;

      setIsEditing(false);
      await fetchNotes(); // Fetch notes after creating
      return true;
    } catch (error: any) {
      toast.error(error.message || 'Failed to create note');
      return false;
    }
  };

  const handleUpdateNote = async (noteParam: Partial<Note>) => {
    if (!selectedNote || !user) return false;

    try {
      const { error } = await supabase
        .from('notes')
        .update({
          title: noteParam.title,
          content: noteParam.content,
          tags: noteParam.tags || []
        })
        .eq('id', selectedNote.id)
        .eq('user_id', user.id);

      if (error) throw error;

      setSelectedNote(null);
      setIsEditing(false);
      await fetchNotes(); // Fetch notes after updating
      return true;
    } catch (error: any) {
      toast.error(error.message || 'Failed to update note');
      return false;
    }
  };

  const handleDeleteNote = async () => {
    if (!noteToDelete || !user) return;

    try {
      const { error } = await supabase
        .from('notes')
        .delete()
        .eq('id', noteToDelete)
        .eq('user_id', user.id);

      if (error) throw error;

      toast.success('Note deleted successfully');
      setNoteToDelete(null);
      setDeleteConfirmOpen(false);
      await fetchNotes(); // Fetch notes after deleting
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete note');
    }
  };

  const handleTogglePublic = async (note: Note) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('notes')
        .update({ is_public: !note.is_public })
        .eq('id', note.id)
        .eq('user_id', user.id);

      if (error) throw error;

      await fetchNotes();
      toast.success(note.is_public ? 'Note is now private' : 'Note is now public');
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const filteredNotes = notes
    .filter((note) => {
      const matchesSearch = note.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        note.content.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesTags = selectedTags.length === 0 ||
        selectedTags.every(tag => note.tags.includes(tag));
      return matchesSearch && matchesTags;
    })
    .sort((a, b) => {
      const [field, order] = sortOption.split('-');
      const aValue = a[field as keyof Note];
      const bValue = b[field as keyof Note];

      if (!aValue || !bValue) return 0;

      if (field === 'title') {
        return order === 'asc'
          ? aValue.toString().localeCompare(bValue.toString())
          : bValue.toString().localeCompare(aValue.toString());
      }

      if (field === 'updatedAt' || field === 'createdAt') {
        const dateA = new Date(aValue).getTime();
        const dateB = new Date(bValue).getTime();
        return order === 'asc' ? dateA - dateB : dateB - dateA;
      }

      return 0;
    });

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-purple-500/5">
      <header className="sticky top-0 border-b bg-background/80 backdrop-blur-sm z-40">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 group">
            <Code2 className="w-6 h-6 text-primary group-hover:text-primary/80 transition-colors" />
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-purple-600">
                CodeNest
              </span>
              <SubscriptionBadge type={subscription} />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <ThemeToggle />
            <Button 
              variant="ghost" 
              onClick={() => navigate('/settings')}
              className="group"
            >
              <Settings className="w-4 h-4 group-hover:rotate-90 transition-transform duration-300" />
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          <aside className="w-full lg:w-72">
            <div className="sticky top-24 space-y-6">
              <div className="flex flex-col gap-4">
                <Button onClick={() => setIsEditing(true)} className="w-full group">
                  <Plus className="w-4 h-4 mr-2 group-hover:rotate-90 transition-transform" />
                  New Note
                </Button>

                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search notes..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <Button
                  variant="ghost"
                  onClick={() => setShowFilters(!showFilters)}
                  className="w-full justify-between group"
                >
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 group-hover:rotate-180 transition-transform" />
                    <span>Filters & Sort</span>
                  </div>
                  <motion.div
                    animate={{ rotate: showFilters ? 180 : 0 }}
                    transition={{ type: "spring", stiffness: 200, damping: 15 }}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </motion.div>
                </Button>

                <AnimatePresence>
                  {showFilters && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="space-y-4 overflow-hidden"
                    >
                      <div>
                        <label className="block text-sm font-medium mb-2">View Mode</label>
                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            variant={viewMode === 'grid' ? 'default' : 'ghost'}
                            size="sm"
                            onClick={() => updateViewMode('grid')}
                            className="w-full"
                          >
                            <Grid className="w-4 h-4 mr-2" />
                            Grid
                          </Button>
                          <Button
                            variant={viewMode === 'list' ? 'default' : 'ghost'}
                            size="sm"
                            onClick={() => updateViewMode('list')}
                            className="w-full"
                          >
                            <List className="w-4 h-4 mr-2" />
                            List
                          </Button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Sort By</label>
                        <select
                          value={sortOption}
                          onChange={(e) => setSortOption(e.target.value)}
                          className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          {SORT_OPTIONS.map(option => (
                            <option key={option.id} value={option.id}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Filter by Tags</label>
                        <div className="space-y-2 max-h-40 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent">
                          {availableTags.map(tag => (
                            <button
                              key={tag.id}
                              onClick={handleTagSelect}
                              className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors hover:bg-muted"
                              style={{
                                backgroundColor: `${tag.color}10`,
                                color: tag.color
                              }}
                            >
                              <span>{tag.name}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div className="space-y-4">
                <h3 className="font-medium text-sm">Quick Actions</h3>
                <div className="space-y-2">
                  <Button 
                    variant="ghost" 
                    className="w-full justify-start group"
                    onClick={() => setShowDisabledDialog(true)}
                  >
                    <Folder className="w-4 h-4 mr-2 group-hover:rotate-12 transition-transform" />
                    Manage Folders
                  </Button>
                  <Button 
                    variant="ghost" 
                    className="w-full justify-start group"
                    onClick={() => setShowDisabledDialog(true)}
                  >
                    <Tag className="w-4 h-4 mr-2 group-hover:rotate-12 transition-transform" />
                    Manage Tags
                  </Button>
                </div>
              </div>
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            <AnimatePresence mode="wait">
              {isLoading ? (
                <LoadingState />
              ) : isEditing ? (
                <motion.div
                  key="editor"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  className="relative min-h-[calc(100vh-12rem)]"
                >
                  <NoteEditor
                    note={selectedNote || undefined}
                    availableTags={availableTags}
                    onSave={selectedNote ? handleUpdateNote : handleCreateNote}
                    onCancel={() => {
                      setSelectedNote(null);
                      setIsEditing(false);
                    }}
                  />
                </motion.div>
              ) : filteredNotes.length === 0 ? (
                <EmptyState
                  title={searchQuery ? "No matching notes" : "Create your first note"}
                  description={searchQuery
                    ? "Try adjusting your search or filters"
                    : "Start organizing your code snippets"
                  }
                  action={{
                    label: "Create Note",
                    onClick: () => setIsEditing(true)
                  }}
                />
              ) : (
                <div className={`grid gap-4 ${
                  viewMode === 'grid'
                    ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3'
                    : 'grid-cols-1'
                }`}>
                  {filteredNotes.map((note) => (
                    <motion.div
                      key={note.id}
                      layout
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                    >
                      <NoteCard
                        note={note}
                        viewMode={viewMode}
                        onEdit={(note) => {
                          setSelectedNote(note);
                          setIsEditing(true);
                        }}
                        onDelete={(noteId) => {
                          setNoteToDelete(noteId);
                          setDeleteConfirmOpen(true);
                        }}
                        onShare={(note) => {
                          setNoteToShare(note);
                          setShowShareDialog(true);
                        }}
                        user={user}
                      />
                    </motion.div>
                  ))}
                </div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </main>

      {showUpdateModal && (
        <UpdateModal
          update={updateData.latestUpdate}
          onClose={handleUpdateModalClose}
        />
      )}

      <DisabledFeatureDialog
        isOpen={showDisabledDialog}
        onClose={() => setShowDisabledDialog(false)}
      />

      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        title="Delete Note"
        description="Are you sure you want to delete this note? This action cannot be undone."
        onConfirm={handleDeleteNote}
        onCancel={() => {
          setNoteToDelete(null);
          setDeleteConfirmOpen(false);
        }}
      />

      {noteToShare && (
        <ShareDialog
          isOpen={showShareDialog}
          onClose={() => {
            setShowShareDialog(false);
            setNoteToShare(null);
          }}
          isPublic={noteToShare.is_public}
          onTogglePublic={() => handleTogglePublic(noteToShare)}
          noteId={noteToShare.id}
        />
      )}
    </div>
  );
};

export default DashboardPage;